/**
 * Session-anchored VWAP with volume-weighted stddev bands.
 * Resets accumulators at each new session (per rthSession dateKey).
 */

function px(bar, field) {
  const n = typeof bar?.[field] === 'number' ? bar[field] : parseFloat(bar?.[field]);
  return Number.isFinite(n) ? n : null;
}

/**
 * @param {Array} bars
 * @param {Array} sessionInfo - from buildSessionIndex(bars)
 * @param {Object} opts
 * @param {number} [opts.bandMult=2.0]
 * @param {number} [opts.innerMult=1.0]
 * @param {number} [opts.armBars=30]
 * @returns {{vwap, up1, dn1, up2, dn2, armed}} per-bar arrays; armed[i] = { side, extPrice, extIdx } | null
 */
export function sessionVwapFromBars(bars, sessionInfo, opts = {}) {
  const bandMult = Number.isFinite(opts.bandMult) ? opts.bandMult : 2.0;
  const innerMult = Number.isFinite(opts.innerMult) ? opts.innerMult : 1.0;
  const armBars = Number.isFinite(opts.armBars) ? opts.armBars : 30;

  const n = bars?.length || 0;
  const vwap = new Array(n).fill(null);
  const up1 = new Array(n).fill(null);
  const dn1 = new Array(n).fill(null);
  const up2 = new Array(n).fill(null);
  const dn2 = new Array(n).fill(null);
  const armed = new Array(n).fill(null);

  let cumV = 0;
  let cumPV = 0;
  let cumP2V = 0;
  let lastDateKey = null;

  // Running "armed" extension state, per active session.
  let state = null; // { side, extPrice, extIdx, reenterIdx }

  for (let i = 0; i < n; i++) {
    const bar = bars[i];
    const info = sessionInfo?.[i];
    if (!info?.isRth) {
      vwap[i] = null; up1[i] = null; dn1[i] = null; up2[i] = null; dn2[i] = null;
      continue;
    }
    if (info.dateKey !== lastDateKey) {
      lastDateKey = info.dateKey;
      cumV = 0; cumPV = 0; cumP2V = 0;
      state = null;
    }

    const h = px(bar, 'high');
    const l = px(bar, 'low');
    const c = px(bar, 'close');
    const v = Number(bar.volume) || 0;
    if (![h, l, c].every(Number.isFinite)) {
      vwap[i] = i > 0 ? vwap[i - 1] : null;
      up1[i] = i > 0 ? up1[i - 1] : null;
      dn1[i] = i > 0 ? dn1[i - 1] : null;
      up2[i] = i > 0 ? up2[i - 1] : null;
      dn2[i] = i > 0 ? dn2[i - 1] : null;
      continue;
    }
    const tp = (h + l + c) / 3;
    cumV += v;
    cumPV += tp * v;
    cumP2V += tp * tp * v;

    const vw = cumV > 0 ? cumPV / cumV : tp;
    const variance = cumV > 0 ? Math.max(0, cumP2V / cumV - vw * vw) : 0;
    const sigma = Math.sqrt(variance);

    vwap[i] = vw;
    up1[i] = vw + innerMult * sigma;
    dn1[i] = vw - innerMult * sigma;
    up2[i] = vw + bandMult * sigma;
    dn2[i] = vw - bandMult * sigma;

    // Extension / arming logic.
    const outLong = l < dn2[i];
    const outShort = h > up2[i];

    if (!state) {
      if (outLong) state = { side: 'long', extPrice: l, extIdx: i, reenterIdx: null };
      else if (outShort) state = { side: 'short', extPrice: h, extIdx: i, reenterIdx: null };
    } else if (state.reenterIdx == null) {
      // still tracking the extreme while outside (or just re-entering) the band
      if (state.side === 'long') {
        if (outLong) {
          if (l < state.extPrice) { state.extPrice = l; state.extIdx = i; }
        } else if (c >= dn2[i]) {
          state.reenterIdx = i;
        }
      } else if (outShort) {
        if (h > state.extPrice) { state.extPrice = h; state.extIdx = i; }
      } else if (c <= up2[i]) {
        state.reenterIdx = i;
      }
    } else if (i - state.reenterIdx > armBars) {
      // armed window expired; allow a brand new extension to arm again
      state = null;
      if (outLong) state = { side: 'long', extPrice: l, extIdx: i, reenterIdx: null };
      else if (outShort) state = { side: 'short', extPrice: h, extIdx: i, reenterIdx: null };
    }

    armed[i] = state && state.reenterIdx != null ? { ...state } : null;
  }

  return { vwap, up1, dn1, up2, dn2, armed };
}
